import { useCallback, useEffect, useRef, useState } from 'react';

import { VoiceNoteBubble } from './VoiceNoteBubble';

export interface PlayableVoiceNoteProps {
  durationSeconds: number;
  levels?: number[];
  documentId?: string;
  isSending?: boolean;
  isFailed?: boolean;
  getDocumentUrl?: (documentId: string) => Promise<string | undefined>;
  transcript?: string;
  isTranscriptShown?: boolean;
  /** Omit to offer no words under the audio. */
  onToggleTranscript?: () => void;
}

export const PlayableVoiceNote = ({
  durationSeconds,
  levels,
  documentId,
  isSending,
  isFailed = false,
  getDocumentUrl,
  transcript,
  isTranscriptShown,
  onToggleTranscript,
}: PlayableVoiceNoteProps) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string>();

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onEnded = () => setIsPlaying(false);
    audio.addEventListener('ended', onEnded);
    return () => audio.removeEventListener('ended', onEnded);
  }, [audioUrl]);

  const handleTogglePlay = useCallback(async () => {
    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
      return;
    }

    let url = audioUrl;
    if (!url && documentId && getDocumentUrl) {
      url = await getDocumentUrl(documentId);
      if (url) setAudioUrl(url);
    }
    if (!url) return;

    if (!audioRef.current) {
      audioRef.current = new Audio(url);
      audioRef.current.addEventListener('ended', () => setIsPlaying(false));
    }
    audioRef.current.play();
    setIsPlaying(true);
  }, [audioUrl, documentId, getDocumentUrl, isPlaying]);

  const canPlay = !!documentId && !!getDocumentUrl && !isSending;

  return (
    <VoiceNoteBubble
      durationSeconds={durationSeconds}
      levels={levels}
      tone={isFailed ? 'failed' : 'filled'}
      isPlaying={isPlaying}
      onTogglePlay={canPlay ? handleTogglePlay : undefined}
      transcript={transcript}
      isTranscriptShown={isTranscriptShown}
      onToggleTranscript={onToggleTranscript}
    />
  );
};

PlayableVoiceNote.displayName = 'PlayableVoiceNote';
export default PlayableVoiceNote;
